import useInteractionsCounts, { useInteractionsCount } from "hooks-for/interactions/use-interactions-counts";
import { COUNT_RANGE_DAYS } from "../../../live/live.constants";
import { endOfDay, subDays } from "date-fns";
import { IInteractionsCount, IInteractionsGetQueryParameters, InteractionState } from "@edgetier/watchtower-types";
import { LIVE_POLL_INTERVAL_MILLISECONDS } from "constants/pages/live";
import { roundDownToInterval } from "utilities/round-down-to-interval";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";

// Update the blocked value every five minutes.
const BLOCKED_POLL_INTERVAL_MILLISECONDS = 300000;

/**
 * Get the start and end date for the next poll. To save the API having to look at dates for all time, the frontend only
 * asks for the queuing/handling etc. interactions in the last few days.
 * @returns Start and end date for the query.
 */
export const getNonBlockedPollParameters = () => ({
    endDateTime: endOfDay(new Date()),
    startDateTime: roundDownToInterval(subDays(new Date(), COUNT_RANGE_DAYS), LIVE_POLL_INTERVAL_MILLISECONDS),
});

/**
 * Count all non-blocked and non-complete interactions. These will change regularly so they are polled with more
 * frequency than the blocked ones which are really slow to calculate.
 * @param nonBlockedPollParameters Poll parameters which will include a moving date range (it updates on each poll).
 * @returns                        Counts of deflecting, handling, queuing, and interactions in wrap-up.
 */
export const useNonBlockedInteractionsCounts = (nonBlockedPollParameters: IInteractionsGetQueryParameters) => {
    const interactionState = [
        InteractionState.Deflecting,
        InteractionState.Handling,
        InteractionState.Queuing,
        InteractionState.WrapUp,
    ];
    const defaults = { interactionState, ...nonBlockedPollParameters };
    const interactionsQueryParameters = useInteractionQueryParameters({ defaults });
    const parameters = { ...interactionsQueryParameters, ...nonBlockedPollParameters, groupBy: "interaction_state" };
    return useInteractionsCounts(parameters, { keepPreviousData: true, select: selectNonBlockedInteractionsCounts });
};

/**
 * Count blocked interactions. This polled less regularly than the other states because it's really slow to calculate.
 * @returns Count of blocked interactions.
 */
export const useBlockedInteractionsCount = () => {
    const interactionState = [InteractionState.Blocked];
    const interactionsQueryParameters = useInteractionQueryParameters({ defaults: { interactionState } });
    return useInteractionsCount(interactionsQueryParameters, {
        keepPreviousData: true,
        refetchInterval: BLOCKED_POLL_INTERVAL_MILLISECONDS,
    });
};

/**
 * From grouped data, return values for interactions being deflected, handled etc.
 * @param counts Count of interactions by state.
 * @returns
 */
const selectNonBlockedInteractionsCounts = (counts: IInteractionsCount<InteractionState>[]) => {
    const byState = new Map(counts.map(({ group, count }) => [group.toUpperCase(), count]));
    return {
        total: counts.reduce((total, { count }) => total + count, 0),
        deflectingCount: byState.get(InteractionState.Deflecting) ?? 0,
        handlingCount: byState.get(InteractionState.Handling) ?? 0,
        queuingCount: byState.get(InteractionState.Queuing) ?? 0,
        wrapUpCount: byState.get(InteractionState.WrapUp) ?? 0,
    };
};
